// ─────────────────────────────────────────────
//  Contact fields — ONE canonical reading of "phone", "email" and "listed"
//  across the three record kinds the Team directory shows: `staff` (people),
//  `surgeons` and `facilities`.
//
//  ⚠️ WHY THIS MODULE EXISTS — THE TWO-PHONE-FIELDS TRAP.
//
//  The v2 contract asks for `phoneNumber` on `users`. `users.phone` ALREADY
//  EXISTS and is already live: ProfilePage writes it, buildDefaultProfile
//  seeds it, scripts/syncStaffAddresses.mjs reads it, and `staff.phone` is the
//  field the board's MD-call panel dials. Creating a second phone field is how
//  a directory starts showing a stale number: two writers, two values, and no
//  rule about which one wins.
//
//  SO: `phone` IS THE CANONICAL STORED FIELD on `users` and `staff`.
//  `phoneNumber` is an ACCEPTED ALIAS on READ ONLY — nothing in this codebase
//  writes it to `users` or `staff`. `contactPhone()` is the one place that
//  knows both names.
//
//  `surgeons` and `facilities` are different, and deliberately so: they are
//  NEW/extended shapes with no legacy `phone` key, so they store the
//  contract's own names — `phoneNumber` (dialable digits) plus `phoneDisplay`
//  (what a human reads). `contactPhone()` reads all three names, so a caller
//  never has to know which kind of record it holds.
//
//  ⚠️ A FACILITY IS REACHED THROUGH A PERSON, AND THAT FIELD ALREADY EXISTS.
//  setupPage.facility.js has written `contact { name, email, phone }` — a
//  REQUIRED group on the Facility Information panel — since long before this
//  module existed, plus the optional `chief` / `viceChief` in the same shape.
//  The v2 schema added the parallel `phoneNumber` / `email` pair for the
//  BUILDING's own line: a switchboard number and a general inbox, which are not
//  the same fact as a named administrator's desk phone.
//
//  Both are real, so both are read, in that order: the facility's own line
//  wins, and `contact` is the fallback. Without the fallback every facility row
//  on the Team page rendered with no phone and no email while the values a
//  human had actually typed sat one key away — a dead binding that passes every
//  test and every glance at the reader. Read what the app WRITES.
//
//  ⚠️ `listed` — ABSENT MEANS LISTED. Check `=== false`, never truthiness. An
//  existing record that predates the flag must not vanish from the directory,
//  and `listed: 0` / `listed: ""` are not opt-outs, they are junk. Only the
//  literal boolean false hides a row.
// ─────────────────────────────────────────────

// The stored field name for a person's phone on `users` and `staff`.
// Documented as a constant so a future writer greps it rather than guessing.
export const CANONICAL_PERSON_PHONE_FIELD = "phone";

// Read-only alias for the same value. NOTHING WRITES THIS on users/staff.
export const PERSON_PHONE_ALIASES = ["phone", "phoneNumber"];

const str = (v) => (typeof v === "string" ? v.trim() : "");

/**
 * The `contact { name, email, phone }` map a facility doc carries — the named
 * person the building is reached through. Setup writes it; see the header.
 * Returns null for every record kind that has no such map (people, surgeons),
 * so the fallbacks below are inert on them.
 *
 * ⚠️ ONLY `contact`, NEVER `chief` / `viceChief`. Those two name the department
 * leadership, not the way in to the building, and quietly printing a chief's
 * mobile as "the facility's number" is a disclosure nobody chose.
 */
function facilityContactMap(record) {
  const c = record && record.contact;
  return c && typeof c === "object" && !Array.isArray(c) ? c : null;
}

/**
 * The dialable phone on any contact-bearing record.
 * Order: the canonical person field, then the contract's alias, then the
 * surgeon/facility display form (better a formatted number than none), and
 * finally the facility's named contact person — the field Setup has always
 * maintained, which is what keeps a facility row from rendering empty.
 */
export function contactPhone(record) {
  if (!record) return "";
  return (
    str(record.phone) ||
    str(record.phoneNumber) ||
    str(record.phoneDisplay) ||
    str(facilityContactMap(record)?.phone)
  );
}

/**
 * What a human should SEE. Falls back to the dialable value, so a record that
 * only ever stored one string still renders.
 */
export function contactPhoneDisplay(record) {
  if (!record) return "";
  return str(record.phoneDisplay) || contactPhone(record);
}

/**
 * The contact email on any of the three record kinds. Lowercased, trimmed.
 * Same two-step as contactPhone: the record's own address first, then the
 * facility's named contact person.
 */
export function contactEmail(record) {
  if (!record) return "";
  return (
    str(record.email) ||
    str(facilityContactMap(record)?.email)
  ).toLowerCase();
}

/**
 * ⚠️ ABSENT MEANS LISTED. Only the literal `false` hides a record — see the
 * header. A record with no `listed` key is listed, which is what keeps every
 * record that predates the flag visible.
 */
export function isListed(record) {
  return !!record && record.listed !== false;
}

/** Drop only the records that explicitly opted out. Tolerant of a non-array. */
export function filterListed(records) {
  return (Array.isArray(records) ? records : []).filter(isListed);
}

/**
 * The contact patch a person's own profile save mirrors onto their linked
 * `staff` record. Deliberately TINY: exactly the two keys firestore.rules
 * lets a non-admin write to their own staff doc. Anything else here would be
 * silently rejected at runtime while every unit test passed.
 */
export function personContactPatch({ phone, email } = {}) {
  return {
    [CANONICAL_PERSON_PHONE_FIELD]: str(phone),
    email: str(email).toLowerCase(),
  };
}
